import type { GridInitialState } from "@mui/x-data-grid-pro";
import { create, type StoreApi } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { resetAllLocalPrefs } from "./createLocalPrefs.ts";
import type { RecentObject } from "./recentObjects.types.ts";
import { RECENT_OBJECTS_MAX_COUNT } from "./recentObjects.types.ts";
import { GLOBAL_CONTEXT_VERSION } from "./useSearchStorage.constants.ts";

const initialLocalstoreState = Object.freeze({
  sideBarCollapsed: false as boolean,
  metricsTreeView: false as boolean,
  namedTFsOnly: false as boolean,
  toggleRefreshState: true as boolean,
  // ponytail: default to browser offset, rounded to whole hours (selector has no half-hour options)
  tz: Math.round(-new Date().getTimezoneOffset() / 60) as number,
  isDebugMode: false as boolean,
  // Debug mode only: the aegra.json graph the AI assistant runs on, or null for the default.
  aiAssistantIdOverride: null as string | null,
  tableCaches: {} as { [key: string]: GridInitialState },
  starredPipelines: {} as Record<string, boolean>,
  starredComputes: {} as Record<string, boolean>,
  env: "" as string,
  recentObjects: [] as RecentObject[],
});

export type GlobalLocalContextSetters = {
  toggleStarredPipeline: (pipelineId: string) => void;
  toggleStarredCompute: (computeId: string) => void;
  toggleSideBarCollapsed: () => void;
  toggleMetricsTreeView: () => void;
  toggleNamedTFsOnly: () => void;
  toggleToggleRefreshState: () => void;
  toggleDebugMode: () => void;
  setAiAssistantIdOverride: (assistantId: string | null) => void;
  setTZ: (tz: number) => void;
  resetAll: () => void;
  addTableCache: (key: string, cache: GridInitialState) => void;
  setEnv: (env: string) => void;
  addRecentObject: (item: RecentObject) => void;
  clearRecentObjects: () => void;
};

type BaseLocalstoreState = typeof initialLocalstoreState & GlobalLocalContextSetters;

type SetFunction = StoreApi<BaseLocalstoreState>["setState"];

const generateSetters = (set: SetFunction): GlobalLocalContextSetters => {
  return {
    setEnv: (env) => set(() => ({ env })),
    toggleStarredPipeline: (pipelineId) =>
      set((state) => {
        const starredPipelines = { ...state.starredPipelines };
        if (starredPipelines[pipelineId]) delete starredPipelines[pipelineId];
        else starredPipelines[pipelineId] = true;
        return { starredPipelines };
      }),
    toggleStarredCompute: (computeId) =>
      set((state) => {
        const starredComputes = { ...state.starredComputes };
        if (starredComputes[computeId]) delete starredComputes[computeId];
        else starredComputes[computeId] = true;
        return { starredComputes };
      }),
    toggleSideBarCollapsed: () =>
      set((state) => ({ sideBarCollapsed: !state.sideBarCollapsed })),
    toggleMetricsTreeView: () =>
      set((state) => ({ metricsTreeView: !state.metricsTreeView })),
    toggleNamedTFsOnly: () =>
      set((state) => ({ namedTFsOnly: !state.namedTFsOnly })),
    toggleToggleRefreshState: () =>
      set((state) => ({ toggleRefreshState: !state.toggleRefreshState })),
    toggleDebugMode: () => set((state) => ({ isDebugMode: !state.isDebugMode })),
    setAiAssistantIdOverride: (aiAssistantIdOverride) =>
      set(() => ({ aiAssistantIdOverride })),
    setTZ: (tz: number) => set(() => ({ tz: tz })),
    resetAll: () => {
      set(initialLocalstoreState);
      // Per-feature preferences (Task Performance, etc.) are not part of this store — they
      // live in their own `definity:prefs:*` keys and would survive an otherwise total reset.
      resetAllLocalPrefs();
    },
    addTableCache: (tableKey: string, cacheState: GridInitialState) => {
      set((state) => {
        const tableCaches = { ...state.tableCaches };
        tableCaches[tableKey] = cacheState;
        return {
          tableCaches,
        };
      });
    },
    addRecentObject: (item: RecentObject) =>
      set((state) => {
        const filtered = state.recentObjects.filter((obj) => obj.id !== item.id);
        return {
          recentObjects: [item, ...filtered].slice(0, RECENT_OBJECTS_MAX_COUNT),
        };
      }),
    clearRecentObjects: () => set({ recentObjects: [] }),
  };
};

type State = typeof initialLocalstoreState & ReturnType<typeof generateSetters>;

export const useGlobalLocalContext = create<State>()(
  persist(
    (set) => ({
      ...initialLocalstoreState,
      ...generateSetters(set),
    }),
    {
      version: GLOBAL_CONTEXT_VERSION,
      name: "global-context",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
